import type { ActivityType } from '../enums/ActivityType'
import type { AICoachPart, AICoachSessionStatus } from './AICoach'
import type { GroupTutorialSummary } from './GroupTutorial'

export type FacilitatorLiveSessionKind = 'game' | 'ai_debrief'

export type FacilitatorLiveSessionPhase = 'planning' | 'execution' | 'reflection'

export type FacilitatorLiveSessionLoopStatus = 'not_started' | 'in_progress' | 'finished'

export interface FacilitatorLiveSessionLoopSummary {
  loopId: string
  loopLabel: string
  displayIndex: number
  startedAt?: string
  finishedAt?: string
  hasStarted: boolean
  hasFinished: boolean
  status: FacilitatorLiveSessionLoopStatus
  currentPhase: FacilitatorLiveSessionPhase
  minuteInLoop: number | null
  participantNames: string[]
  participantCount: number
}

export interface FacilitatorLiveSessionParticipant {
  userId?: string
  name: string
  /** Connected to the activity's call right now (live Whereby presence). */
  inMeeting: boolean
  /** Acted in the current loop, or in the group tutorial before the first one; false on a debrief card. */
  playedThisLoop: boolean
  /** The team they play in, on a card that lists the whole sprint. */
  teamName?: string
}

export interface FacilitatorLiveSessionCard {
  cardId: string
  kind: FacilitatorLiveSessionKind
  sprintId: string
  sprintTitle: string
  activityId: string
  activityTitle: string
  teamId: string
  teamName: string
  startDate: string
  endDate: string
  expectedCount: number
  /** Game cards: how many played the current loop. */
  playedCount?: number
  // Live Whereby presence (who is connected to the call right now), distinct from game-event activity.
  inMeetCount?: number
  // One row per expected participant, with both live signals resolved against the roster by user id.
  participants: FacilitatorLiveSessionParticipant[]
  // Configured loop length for a game activity, in whole minutes.
  loopLengthMinutes?: number
  stageLabel: string
  // Scheduled start passed but the game/debrief has not begun.
  isDelayed?: boolean
  currentLoopNumber?: number
  totalLoops?: number
  currentPhase?: FacilitatorLiveSessionPhase
  minuteInLoop?: number
  loopSummaries?: FacilitatorLiveSessionLoopSummary[]
  // The group tutorial runs before the first loop exists, so it is the only signal a game has begun at all.
  groupTutorial?: GroupTutorialSummary
  aiCoachSessionId?: string
  aiCoachStatus?: AICoachSessionStatus
  currentPart?: AICoachPart | null
  completedParts?: AICoachPart[]
  canJoinAiDebrief?: boolean
}

/** An activity every team of the sprint holds together (the introduction, a group reflection) in one room. */
export interface FacilitatorSprintCallCard {
  cardId: string
  sprintId: string
  activityId: string
  activityType: ActivityType
  activityTitle: string
  startDate: string
  endDate: string
  /** One row per person in the sprint, with whether they are in the room. */
  participants: FacilitatorLiveSessionParticipant[]
}

/** How many people of the sprint are in its room right now. */
export const countSprintCallMembersInRoom = (sprintCall: FacilitatorSprintCallCard): number =>
  sprintCall.participants.filter((participant) => participant.inMeeting).length

export interface FacilitatorLiveSessionSprintSection {
  sprintId: string
  sprintTitle: string
  cards: FacilitatorLiveSessionCard[]
  sprintCalls: FacilitatorSprintCallCard[]
}

export interface FacilitatorGameCallLinkRequest {
  sprintId: string
  activityId: string
  teamId: string
}

export interface FacilitatorGameCallLinkResponse {
  success: boolean
  /** The team's room link with this facilitator's name and app user id on it, ready to open in a new tab. */
  roomUrl: string
  roomName: string
  teamName: string
}

export interface FacilitatorSprintCallLinkResponse {
  success: boolean
  /** The sprint room's link with this facilitator's name and app user id on it, ready to open in a new tab. */
  roomUrl: string
  roomName: string
}

export interface GetFacilitatorLiveSessionMonitorResponse {
  success: boolean
  generatedAt: string
  sections: FacilitatorLiveSessionSprintSection[]
  error?: string
}
